import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Course, ExamOption, ExamQuestion } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';

type QuestionDraft = ExamQuestion & { key: string };

const emptyOption = (): ExamOption => ({ text: '', is_correct: false });

const emptyQuestion = (): QuestionDraft => ({
  key: crypto.randomUUID(),
  text: '',
  question_type: 'single',
  options: [emptyOption(), emptyOption()],
});

export default function CreateExamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseId, setCourseId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timed, setTimed] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState('30');
  const [questions, setQuestions] = useState<QuestionDraft[]>([emptyQuestion()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== 'tutor' && user?.role !== 'admin') {
      navigate('/exams');
      return;
    }
    (async () => {
      const response = await apiClient.getCourses();
      if (response.success && response.data?.results) {
        const mine = response.data.results.filter(
          (c) => c.tutor?.id === user.id || user.role === 'admin'
        );
        setCourses(mine);
        if (mine.length === 1) setCourseId(mine[0].id);
      }
    })();
  }, [user, navigate]);

  const updateQuestion = (key: string, patch: Partial<ExamQuestion>) => {
    setQuestions((prev) =>
      prev.map((q) => (q.key === key ? { ...q, ...patch } : q))
    );
  };

  const updateOption = (qKey: string, index: number, patch: Partial<ExamOption>) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.key !== qKey) return q;
        const options = q.options.map((opt, i) => (i === index ? { ...opt, ...patch } : opt));
        if (patch.is_correct && q.question_type === 'single') {
          return {
            ...q,
            options: options.map((opt, i) => ({ ...opt, is_correct: i === index })),
          };
        }
        return { ...q, options };
      })
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId || !title.trim()) {
      setError('Course and title are required');
      return;
    }
    if (timed) {
      const minutes = Number(durationMinutes);
      if (!Number.isInteger(minutes) || minutes < 1) {
        setError('Timed exams need a duration of at least 1 minute');
        return;
      }
    }
    for (const q of questions) {
      if (!q.text.trim() || q.options.length < 2) {
        setError('Each question needs text and at least two options');
        return;
      }
      if (!q.options.some((o) => o.is_correct)) {
        setError('Each question needs at least one correct option');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      const payload = {
        course: courseId,
        title,
        description,
        duration_minutes: timed ? Number(durationMinutes) : null,
        total_marks: questions.length,
        questions: questions.map(({ text, question_type, options }) => ({
          text,
          question_type,
          options: options.map((o) => ({
            text: o.text,
            is_correct: !!o.is_correct,
          })),
        })),
      };
      const response = await apiClient.createExam(payload);
      if (response.success && response.data?.id) {
        navigate(`/exams/${response.data.id}`);
      } else {
        setError(response.error || 'Failed to create exam');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Create Exam</h1>
        <Link to="/exams" className="text-blue-600 hover:underline text-sm">
          Back to exams
        </Link>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg shadow p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value ? Number(e.target.value) : '')}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          >
            <option value="">Select a course</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <input
              type="checkbox"
              checked={timed}
              onChange={(e) => setTimed(e.target.checked)}
            />
            Timed exam
          </label>
          {timed && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
              <input
                type="number"
                min={1}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                className="w-40 border border-gray-300 rounded-md px-3 py-2"
                required
              />
              <p className="mt-1 text-xs text-slate-500">The timer starts when the student confirms and cannot be paused.</p>
            </div>
          )}
          {!timed && (
            <p className="text-xs text-slate-500">Students can take as long as they need.</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Questions</h2>
            <button
              type="button"
              onClick={() => setQuestions((prev) => [...prev, emptyQuestion()])}
              className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-md"
            >
              Add question
            </button>
          </div>

          {questions.map((q, qIndex) => (
            <div key={q.key} className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-800">Question {qIndex + 1}</span>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setQuestions((prev) => prev.filter((item) => item.key !== q.key))}
                    className="text-sm text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>

              <textarea
                value={q.text}
                onChange={(e) => updateQuestion(q.key, { text: e.target.value })}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Question text"
                required
              />

              <select
                value={q.question_type}
                onChange={(e) =>
                  updateQuestion(q.key, {
                    question_type: e.target.value as ExamQuestion['question_type'],
                  })
                }
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="single">Single choice</option>
                <option value="multiple">Multiple choice</option>
                <option value="true_false">True / False</option>
              </select>

              <div className="space-y-2">
                {q.options.map((opt, optIndex) => (
                  <div key={optIndex} className="flex items-center gap-2">
                    <input
                      type={q.question_type === 'multiple' ? 'checkbox' : 'radio'}
                      name={`correct-${q.key}`}
                      checked={!!opt.is_correct}
                      onChange={(e) =>
                        updateOption(q.key, optIndex, { is_correct: e.target.checked })
                      }
                      title="Mark as correct"
                    />
                    <input
                      value={opt.text}
                      onChange={(e) => updateOption(q.key, optIndex, { text: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder={`Option ${optIndex + 1}`}
                      required
                    />
                    {q.options.length > 2 && (
                      <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() =>
                          setQuestions((prev) =>
                            prev.map((item) =>
                              item.key === q.key
                                ? {
                                    ...item,
                                    options: item.options.filter((_, i) => i !== optIndex),
                                  }
                                : item
                            )
                          )
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="text-sm text-blue-600"
                  onClick={() =>
                    setQuestions((prev) =>
                      prev.map((item) =>
                        item.key === q.key
                          ? { ...item, options: [...item.options, emptyOption()] }
                          : item
                      )
                    )
                  }
                >
                  Add option
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-md"
        >
          {saving ? 'Creating…' : 'Create exam'}
        </button>
      </form>
    </div>
  );
}
